import { useCallback, useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { selectVehiclesOneGraph } from '../../redux/vehiclesReducer';
import { selectAppConfig } from '../../redux/appConfigReducer';
import { selectFilters } from '../../redux/filtersReducer';
import getParsedNumber from '../../utils/getParsedNumber';
import {
  BODY_FILTERS_LIST_NEW,
  BODY_FILTERS_LIST_USED,
  CA_TRIMLINE_FILTERS,
  DRIVE_FILTERS,
  EXT_COLOR_FILTERS,
  FUEL_FILTERS_NEW,
  FUEL_FILTERS_USED,
  GEARBOX_FILTERS,
  INT_COLOR_FILTERS,
  MILEAGE_FILTERS,
  POWER_FILTERS,
  PRICE_FILTERS_NEW,
  PRICE_FILTERS_USED,
  US_TRIMLINE_FILTERS,
  WHEEL_SIZE_FILTERS,
} from '../../constants/listOfFilters';
import nonAudiBrandsFullList from '../../constants/listOfNonAudiBrands.json';
import { SearchCriteriaType } from '../../types/oneGraphTypes';

export interface NonAudiModelFullList {
  [key: string]: {
    text: string;
  };
}

const useFilterOptions = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.languages?.[0] || 'en';

  const { availableFilters, totalCount } = useSelector(selectVehiclesOneGraph);
  const { confirmedFilters } = useSelector(selectFilters);
  const { marketType, listOfDealers, isTier3page, filterGroupsToRender } =
    useSelector(selectAppConfig);

  const isUsedVehiclesSelected = confirmedFilters.vehicleType === 'used';

  const [allUsedCount, setAllUsedCount] = useState(0);
  const [allAudiUsedCount, setAllAudiUsedCount] = useState(0);
  const [cpoCount, setCpoCount] = useState(0);
  const [usedCount, setUsedCount] = useState(0);
  const [nonAudiCountT3, setNonAudiCountT3] = useState(0);

  const getFilterListWithCounts = (
    filterList: string[],
    possibleOptions: SearchCriteriaType | undefined,
    isTagRemovable: boolean,
  ) => {
    return filterList.map((filterValue) => {
      const filterData = possibleOptions?.possibleItems.find(
        (toCheck) => toCheck.id === filterValue,
      );
      return {
        id: filterValue,
        value: filterValue,
        count: filterData ? filterData.number : 0,
        isFilterTagNotRemovable: isTagRemovable,
        disabled: !filterData,
      };
    });
  };

  useEffect(() => {
    if (
      (confirmedFilters.nonAudi === 'foreign-brand.yes' ||
        confirmedFilters.usedInventoryType === 'has-warranties.nonAudi' ||
        confirmedFilters.usedInventoryType === 'has-warranties.combined') &&
      !confirmedFilters.otherMakes?.length
    ) {
      const nonAudiOptions = availableFilters
        ?.find((toCheck) => toCheck.id === 'foreign-brand')
        ?.possibleItems?.find((item) => item?.id === 'yes');
      setNonAudiCountT3(nonAudiOptions?.number ?? 0);
    }
  }, [
    availableFilters,
    confirmedFilters.nonAudi,
    confirmedFilters.otherMakes?.length,
    confirmedFilters.usedInventoryType,
  ]);

  const isCPOActive = confirmedFilters.usedInventoryType === 'has-warranties.yes';
  const isUsedActive = confirmedFilters.usedInventoryType === 'has-warranties.no';

  // Tier 1 Counts Logic
  useEffect(() => {
    if (isTier3page) return;
    if (
      isUsedVehiclesSelected &&
      confirmedFilters.usedInventoryType === 'has-warranties.all' &&
      !isCPOActive &&
      !isUsedActive
    ) {
      setAllAudiUsedCount(totalCount ?? 0);
      const cpoOptions = availableFilters
        ?.find((toCheck) => toCheck.id === 'has-warranties')
        ?.possibleItems?.find((item) => item?.id === '1');
      setCpoCount(cpoOptions?.number ?? 0);
      const preOwnedOnlyOptions = availableFilters
        ?.find((toCheck) => toCheck.id === 'has-warranties')
        ?.possibleItems?.find((item) => item?.id === '0');
      setUsedCount(preOwnedOnlyOptions?.number ?? 0);
    }
  }, [
    availableFilters,
    confirmedFilters.usedInventoryType,
    isCPOActive,
    isTier3page,
    isUsedActive,
    isUsedVehiclesSelected,
    totalCount,
  ]);

  // Tier 3 Counts Logic
  useEffect(() => {
    if (!isTier3page) return;
    if (
      (confirmedFilters.usedInventoryType === 'has-warranties.all' ||
        confirmedFilters.usedInventoryType === 'has-warranties.combined') &&
      !isCPOActive &&
      !isUsedActive
    ) {
      setAllAudiUsedCount(totalCount ?? 0);
      const cpoTotalCount =
        availableFilters
          ?.find((toCheck) => toCheck.id === 'has-warranties')
          ?.possibleItems?.find((item) => item?.id === '1')?.number || 0;

      const allNonAudiCount =
        availableFilters
          ?.find((toCheck) => toCheck.id === 'foreign-brand')
          ?.possibleItems?.find((item) => item?.id === 'yes')?.number || 0;

      const allAudiCount =
        availableFilters
          ?.find((toCheck) => toCheck.id === 'foreign-brand')
          ?.possibleItems?.find((item) => item?.id === 'no')?.number || 0;

      setAllUsedCount(allNonAudiCount + allAudiCount);
      setAllAudiUsedCount(allAudiCount);
      setCpoCount(cpoTotalCount);
      // setUsedCount(filterResults?.has_warranties?.no?.count);
      setNonAudiCountT3(allNonAudiCount);
    }
  }, [
    availableFilters,
    confirmedFilters.usedInventoryType,
    cpoCount,
    isCPOActive,
    isTier3page,
    isUsedActive,
    totalCount,
  ]);

  const isUSMarket = marketType !== 'CAN';

  const transformArrayFilters = useCallback(
    (
      arrayWithFilterValues: number[] | undefined,
      filterType: string,
      isFilterTagNotRemovable: boolean,
    ) => {
      if (!arrayWithFilterValues) return {};
      return Object.fromEntries(
        arrayWithFilterValues.map((filterValue) => [
          filterValue,
          {
            value: filterValue,
            label: t(`vlp.filters.filterOptions.${filterType}.${filterValue}`),
            ariaLabel: t(`vlp.filters.filterOptions.${filterType}.${filterValue}`),
            isFilterTagNotRemovable,
          },
        ]),
      );
    },
    [t],
  );

  // --------------------------------------------------------------------------------
  // -------------------- VEHICLE TYPE FILTERS  -------------------------------------

  const vehicleTypeOptions = useMemo(
    () => ({
      new: {
        label: t('vlp.filters.filterOptions.vehicleType.new'),
        ariaLabel: t('vlp.filters.filterOptions.vehicleType.new'),
        value: 'new',
      },
      used: {
        label: t('vlp.filters.filterOptions.vehicleType.used'),
        ariaLabel: t('vlp.filters.filterOptions.vehicleType.used'),
        value: 'used',
      },
    }),
    [t],
  );

  const newInventoryTypeOptions = useMemo(() => {
    return {
      availableInMonths0: {
        label: t('vlp.filters.vehicelStatus.new.availableInMonths0'),
        ariaLabel: t('vlp.filters.vehicelStatus.new.availableInMonths0'),
        value: '0',
      },
      availableInMonths3: {
        label: t('vlp.filters.vehicelStatus.new.availableInMonths3'),
        ariaLabel: t('vlp.filters.vehicelStatus.new.availableInMonths3'),
        value: '3',
      },
    };
  }, [t]);

  const usedInventoryTypeOptions = useMemo(() => {
    const options = {
      all: {
        label: t('vlp.filters.vehicelStatus.cpo.allPreOwned'),
        ariaLabel: t('vlp.filters.vehicelStatus.cpo.allPreOwned', {
          hasWarrantiesAll: getParsedNumber(allAudiUsedCount ?? 0, lang),
        }),
        value: 'has-warranties.all',
        popoverDescription: t(
          `vlp.filters.filterOptions.usedInventoryType.${isUSMarket ? 'allPreOwnedUSA' : 'allPreOwnedCAN'}.popoverDescription`,
        ),
        isNonAudiFilter: false,
        count: allAudiUsedCount,
      },
      yes: {
        label: t('vlp.filters.vehicelStatus.cpo.cpoOnly'),
        ariaLabel: t('vlp.filters.vehicelStatus.cpo.cpoOnly', {
          has_warranties_yes: getParsedNumber(cpoCount ?? 0, lang),
        }),
        value: 'has-warranties.yes',
        popoverDescription: t(
          `vlp.filters.filterOptions.usedInventoryType.${isUSMarket ? 'certifiedPreOwned' : 'certifiedPlus'}.popoverDescription`,
        ),
        isNonAudiFilter: false,
        count: cpoCount,
      },
      no: {
        label: t('vlp.filters.vehicelStatus.cpo.preOwnedOnly'),
        ariaLabel: t('vlp.filters.vehicelStatus.cpo.preOwnedOnly', {
          has_warranties_no: getParsedNumber(usedCount ?? 0, lang),
        }),
        value: 'has-warranties.no',
        popoverDescription: t(
          'vlp.filters.filterOptions.usedInventoryType.preOwned.popoverDescription',
        ),
        isNonAudiFilter: false,
        count: usedCount,
      },
    };

    const tier3Options = {
      all: {
        label: t('vlp.filters.vehicelStatus.cpo.allPreOwnedTier3'),
        // FIX LATER
        ariaLabel: t('vlp.filters.vehicelStatus.cpo.allPreOwnedTier3', {
          hasWarrantiesAll: getParsedNumber(allUsedCount ?? 0, lang),
        }),
        value: 'has-warranties.all',
        popoverDescription: t(
          `vlp.filters.filterOptions.usedInventoryType.allPreOwned${isUSMarket ? 'USA' : 'CAN'}Tier3.popoverDescription`,
        ),
        isNonAudiFilter: false,
        count: allAudiUsedCount,
      },
      yes: {
        label: t('vlp.filters.vehicelStatus.cpo.cpoOnly'),
        ariaLabel: t('vlp.filters.vehicelStatus.cpo.cpoOnly', {
          has_warranties_yes: getParsedNumber(cpoCount ?? 0, lang),
        }),
        value: 'has-warranties.yes',
        popoverDescription: t(
          `vlp.filters.filterOptions.usedInventoryType.${isUSMarket ? 'certifiedPreOwned' : 'certifiedPlus'}.popoverDescription`,
        ),
        isNonAudiFilter: false,
        count: cpoCount,
      },
      nonAudi: {
        label: t(`vlp.filters.filterOptions.vehicleType.nonAudi`),
        ariaLabel: t(`vlp.filters.filterOptions.vehicleType.nonAudi`),
        value: 'has-warranties.nonAudi',
        isNonAudiFilter: true,
        count: nonAudiCountT3,
      },
      combined: {
        label: t('vlp.filters.vehicelStatus.cpo.allPreOwned'),
        // FIX Later
        ariaLabel: t(`vlp.filters.filterOptions.vehicleType.allPreOwned`),
        value: 'has-warranties.combined',
        isNonAudiFilter: false,
        count: allUsedCount,
      },
    };

    return isTier3page ? tier3Options : options;
  }, [
    t,
    allUsedCount,
    lang,
    isUSMarket,
    cpoCount,
    usedCount,
    allAudiUsedCount,
    nonAudiCountT3,
    isTier3page,
  ]);

  // -----------------------------------------------------------------------------
  // -------------------- BODY TYPE FILTERS  -------------------------------------

  const bodyTypeOptions = useMemo(() => {
    const bodyTypePossibleOptions = availableFilters?.find((toCheck) => toCheck.id === 'body-type');
    const isFilterTagNotRemovable = isTier3page && !filterGroupsToRender?.includes('bodyType');

    const newBodyTypes = getFilterListWithCounts(
      BODY_FILTERS_LIST_NEW,
      bodyTypePossibleOptions,
      isFilterTagNotRemovable,
    );
    const usedBodyTypes = getFilterListWithCounts(
      BODY_FILTERS_LIST_USED,
      bodyTypePossibleOptions,
      isFilterTagNotRemovable,
    );

    const bodyTypesWithCounts = isUsedVehiclesSelected ? usedBodyTypes : newBodyTypes;

    return bodyTypesWithCounts;
  }, [availableFilters, filterGroupsToRender, isTier3page, isUsedVehiclesSelected]);

  // -----------------------------------------------------------------------------
  // -------------------- MODEL FAMILY FILTERS -----------------------------------

  const modelFamilyOptions = useMemo(() => {
    const modelFamilyPossibleOptions = availableFilters?.find(
      (toCheck) => toCheck.id === 'model-range',
    );
    const isFilterTagNotRemovable = isTier3page && !filterGroupsToRender?.includes('modelFamily');
    const isNew = confirmedFilters.vehicleType === 'new';
    const isHidden = isNew;
    return {
      etron: {
        value: 'etron',
        id: 'etron',
        tabs: ['etron'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find(
          (toCheck) => toCheck.id === 'etron',
        )?.number,
        isFilterTagNotRemovable,
      },
      etrongt: {
        value: 'etrongt',
        id: 'etrongt',
        tabs: ['etron'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find(
          (toCheck) => toCheck.id === 'etrongt',
        )?.number,
        isFilterTagNotRemovable,
      },
      rsetrongt: {
        value: 'rsetrongt',
        id: 'rsetrongt',
        tabs: ['etron'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find(
          (toCheck) => toCheck.id === 'rsetrongt',
        )?.number,
        isFilterTagNotRemovable,
      },
      a6etron: {
        value: 'a6etron',
        id: 'a6etron',
        tabs: ['etron'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find(
          (toCheck) => toCheck.id === 'a6etron',
        )?.number,
        isFilterTagNotRemovable,
      },
      s6etron: {
        value: 's6etron',
        id: 's6etron',
        tabs: ['etron'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find(
          (toCheck) => toCheck.id === 's6etron',
        )?.number,
        isFilterTagNotRemovable,
      },
      q4: {
        value: 'q4',
        id: 'q4',
        tabs: ['etron'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find((toCheck) => toCheck.id === 'q4')
          ?.number,
        isFilterTagNotRemovable,
      },
      q6etron: {
        value: 'q6etron',
        id: 'q6etron',
        tabs: ['etron'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find(
          (toCheck) => toCheck.id === 'q6etron',
        )?.number,
        isFilterTagNotRemovable,
      },
      sq6etron: {
        value: 'sq6etron',
        id: 'sq6etron',
        tabs: ['etron'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find(
          (toCheck) => toCheck.id === 'sq6etron',
        )?.number,
        isFilterTagNotRemovable,
      },
      q8etron: {
        value: 'q8etron',
        id: 'q8etron',
        tabs: ['etron'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find(
          (toCheck) => toCheck.id === 'q8etron',
        )?.number,
        isFilterTagNotRemovable,
      },
      sq8etron: {
        value: 'sq8etron',
        id: 'sq8etron',
        tabs: ['etron'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find(
          (toCheck) => toCheck.id === 'sq8etron',
        )?.number,
        isFilterTagNotRemovable,
      },
      q3: {
        value: 'q3',
        id: 'q3',
        tabs: ['q/sq'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find((toCheck) => toCheck.id === 'q3')
          ?.number,
        isFilterTagNotRemovable,
      },
      'q5-new': {
        value: 'q5-new',
        id: 'q5-new',
        tabs: ['q/sq'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find(
          (toCheck) => toCheck.id === 'q5-new',
        )?.number,
        isFilterTagNotRemovable,
        isHidden: !isNew,
      },
      'sq5-new': {
        value: 'sq5-new',
        id: 'sq5-new',
        tabs: ['q/sq'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find(
          (toCheck) => toCheck.id === 'sq5-new',
        )?.number,
        isFilterTagNotRemovable,
        isHidden: !isNew,
      },
      q5: {
        value: 'q5',
        id: 'q5',
        tabs: ['q/sq'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find((toCheck) => toCheck.id === 'q5')
          ?.number,
        isFilterTagNotRemovable,
      },
      q5tfsie: {
        value: 'q5tfsie',
        id: 'q5tfsie',
        tabs: ['q/sq'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find(
          (toCheck) => toCheck.id === 'q5tfsie',
        )?.number,
        isFilterTagNotRemovable,
      },
      sq5: {
        value: 'sq5',
        id: 'sq5',
        tabs: ['q/sq'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find(
          (toCheck) => toCheck.id === 'sq5',
        )?.number,
        isFilterTagNotRemovable,
      },
      q7: {
        value: 'q7',
        id: 'q7',
        tabs: ['q/sq'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find((toCheck) => toCheck.id === 'q7')
          ?.number,
        isFilterTagNotRemovable,
      },
      sq7: {
        value: 'sq7',
        id: 'sq7',
        tabs: ['q/sq'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find(
          (toCheck) => toCheck.id === 'sq7',
        )?.number,
        isFilterTagNotRemovable,
      },
      q8: {
        value: 'q8',
        id: 'q8',
        tabs: ['q/sq'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find((toCheck) => toCheck.id === 'q8')
          ?.number,
        isFilterTagNotRemovable,
      },
      sq8: {
        value: 'sq8',
        id: 'sq8',
        tabs: ['q/sq'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find(
          (toCheck) => toCheck.id === 'sq8',
        )?.number,
        isFilterTagNotRemovable,
      },
      rsq8: {
        value: 'rsq8',
        id: 'rsq8',
        tabs: ['q/sq'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find(
          (toCheck) => toCheck.id === 'rsq8',
        )?.number,
        isFilterTagNotRemovable,
      },
      a3: {
        value: 'a3',
        id: 'a3',
        tabs: ['a/s'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find((toCheck) => toCheck.id === 'a3')
          ?.number,
        isFilterTagNotRemovable,
      },
      s3: {
        value: 's3',
        id: 's3',
        tabs: ['a/s'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find((toCheck) => toCheck.id === 's3')
          ?.number,
        isFilterTagNotRemovable,
      },
      rs3: {
        value: 'rs3',
        id: 'rs3',
        tabs: ['a/s'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find(
          (toCheck) => toCheck.id === 'rs3',
        )?.number,
        isFilterTagNotRemovable,
      },
      a4: {
        value: 'a4',
        id: 'a4',
        tabs: ['a/s'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find((toCheck) => toCheck.id === 'a4')
          ?.number,
        isFilterTagNotRemovable,
      },
      s4: {
        value: 's4',
        id: 's4',
        tabs: ['a/s'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find((toCheck) => toCheck.id === 's4')
          ?.number,
        isFilterTagNotRemovable,
      },
      'a5-new': {
        value: 'a5-new',
        id: 'a5-new',
        tabs: ['a/s'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find(
          (toCheck) => toCheck.id === 'a5-new',
        )?.number,
        isFilterTagNotRemovable,
        isHidden: !isNew,
      },
      's5-new': {
        value: 's5-new',
        id: 's5-new',
        tabs: ['a/s'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find(
          (toCheck) => toCheck.id === 's5-new',
        )?.number,
        isFilterTagNotRemovable,
        isHidden: !isNew,
      },
      a5: {
        value: 'a5',
        id: 'a5',
        tabs: ['a/s'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find((toCheck) => toCheck.id === 'a5')
          ?.number,
        isFilterTagNotRemovable,
      },
      s5: {
        value: 's5',
        id: 's5',
        tabs: ['a/s'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find((toCheck) => toCheck.id === 's5')
          ?.number,
        isFilterTagNotRemovable,
      },
      rs5: {
        value: 'rs5',
        id: 'rs5',
        tabs: ['a/s'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find(
          (toCheck) => toCheck.id === 'rs5',
        )?.number,
        isFilterTagNotRemovable,
      },
      a6: {
        value: 'a6',
        id: 'a6',
        tabs: ['a/s'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find((toCheck) => toCheck.id === 'a6')
          ?.number,
        isFilterTagNotRemovable,
      },
      s6: {
        value: 's6',
        id: 's6',
        tabs: ['a/s'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find((toCheck) => toCheck.id === 's6')
          ?.number,
        isFilterTagNotRemovable,
      },
      rs6: {
        value: 'rs6',
        id: 'rs6',
        tabs: ['a/s'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find(
          (toCheck) => toCheck.id === 'rs6',
        )?.number,
        isFilterTagNotRemovable,
      },
      a7: {
        value: 'a7',
        id: 'a7',
        tabs: ['a/s'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find((toCheck) => toCheck.id === 'a7')
          ?.number,
        isFilterTagNotRemovable,
      },
      s7: {
        value: 's7',
        id: 's7',
        tabs: ['a/s'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find((toCheck) => toCheck.id === 's7')
          ?.number,
        isFilterTagNotRemovable,
      },
      rs7: {
        value: 'rs7',
        id: 'rs7',
        tabs: ['a/s'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find(
          (toCheck) => toCheck.id === 'rs7',
        )?.number,
        isFilterTagNotRemovable,
      },
      a8: {
        value: 'a8',
        id: 'a8',
        tabs: ['a/s'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find((toCheck) => toCheck.id === 'a8')
          ?.number,
        isFilterTagNotRemovable,
      },
      s8: {
        value: 's8',
        id: 's8',
        tabs: ['a/s'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find((toCheck) => toCheck.id === 's8')
          ?.number,
        isFilterTagNotRemovable,
      },
      r8: {
        value: 'r8',
        id: 'r8',
        tabs: ['r/tt'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find((toCheck) => toCheck.id === 'r8')
          ?.number,
        isFilterTagNotRemovable,
      },
      tt: {
        value: 'tt',
        id: 'tt',
        tabs: ['r/tt'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find((toCheck) => toCheck.id === 'tt')
          ?.number,
        isFilterTagNotRemovable,
        isHidden,
      },
      tts: {
        value: 'tts',
        id: 'tts',
        tabs: ['r/tt'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find(
          (toCheck) => toCheck.id === 'tts',
        )?.number,
        isFilterTagNotRemovable,
        isHidden,
      },
      ttrs: {
        value: 'ttrs',
        id: 'ttrs',
        tabs: ['r/tt'],
        disabled: !modelFamilyPossibleOptions?.possibleItems?.find(
          (toCheck) => toCheck.id === 'ttrs',
        )?.number,
        isFilterTagNotRemovable,
        isHidden,
      },
    };
  }, [availableFilters, confirmedFilters.vehicleType, filterGroupsToRender, isTier3page]);

  // -----------------------------------------------------------------------------
  // -------------------- COLOR FILTERS WITH IMAGES ------------------------------

  const exteriorColorOptions = useMemo(() => {
    const exteriorPossibleOptions = availableFilters?.find(
      (toCheck) => toCheck.id === 'exterior-color',
    );
    const isFilterTagNotRemovable = isTier3page && !filterGroupsToRender?.includes('fuelType');

    const colorsWithCounts = EXT_COLOR_FILTERS.map((colorValue) => {
      const colorData = exteriorPossibleOptions?.possibleItems.find(
        (toCheck) => toCheck.id === colorValue,
      );
      return {
        id: colorValue,
        value: colorValue,
        count: colorData ? colorData.number : 0,
        isFilterTagNotRemovable,
        disabled: !colorData,
        image: `https://nar.media.audi.com/is/image/audinar/nemo/ca/NoMo/collaboration/wormwod/colour-tiles/exterior-color.${colorValue}.png`,
      };
    });

    return colorsWithCounts;
  }, [availableFilters, filterGroupsToRender, isTier3page]);

  const interiorColorOptions = useMemo(() => {
    const interiorPossibleOptions = availableFilters?.find(
      (toCheck) => toCheck.id === 'interior-color-seats',
    );
    const isFilterTagNotRemovable = isTier3page && !filterGroupsToRender?.includes('fuelType');

    const colorsWithCounts = INT_COLOR_FILTERS.map((colorValue) => {
      const colorData = interiorPossibleOptions?.possibleItems.find(
        (toCheck) => toCheck.id === colorValue,
      );
      return {
        id: colorValue,
        value: colorValue,
        count: colorData ? colorData.number : 0,
        isFilterTagNotRemovable,
        disabled: !colorData,
        image: `https://nar.media.audi.com/is/image/audinar/nemo/ca/NoMo/collaboration/wormwod/colour-tiles/exterior-color.${colorValue}.png`,
      };
    });

    return colorsWithCounts;
  }, [availableFilters, filterGroupsToRender, isTier3page]);

  // -----------------------------------------------------------------------------
  // -------------------- REGULAR CHECKBOX FILTERS -------------------------------

  const trimlineOptions = useMemo(() => {
    const trimlinePossibleOptions = availableFilters?.find((toCheck) => toCheck.id === 'trimline');
    const isFilterTagNotRemovable = isTier3page && !filterGroupsToRender?.includes('trimLine');
    const availableTrimValues = isUSMarket ? US_TRIMLINE_FILTERS : CA_TRIMLINE_FILTERS;

    const trimlinesWithCounts = availableTrimValues.map((trimValue) => {
      const trimlineData = trimlinePossibleOptions?.possibleItems.find(
        (toCheck) => toCheck.id === trimValue,
      );
      return {
        id: trimValue,
        value: trimValue,
        count: trimlineData ? trimlineData.number : 0,
        isFilterTagNotRemovable,
        disabled: !trimlineData,
      };
    });

    return trimlinesWithCounts;
  }, [availableFilters, filterGroupsToRender, isTier3page, isUSMarket]);

  const fuelTypeOptions = useMemo(() => {
    const fuelPossibleOptions = availableFilters?.find(
      (toCheck) => toCheck.id === 'vtp-drivetrain',
    );
    const isFilterTagNotRemovable = isTier3page && !filterGroupsToRender?.includes('fuelType');

    const newFuelFilters = getFilterListWithCounts(
      FUEL_FILTERS_NEW,
      fuelPossibleOptions,
      isFilterTagNotRemovable,
    );

    const usedFuelFilters = getFilterListWithCounts(
      FUEL_FILTERS_USED,
      fuelPossibleOptions,
      isFilterTagNotRemovable,
    );

    const fuleTypesWithCounts = isUsedVehiclesSelected ? usedFuelFilters : newFuelFilters;

    return fuleTypesWithCounts;
  }, [availableFilters, filterGroupsToRender, isTier3page, isUsedVehiclesSelected]);

  const drivetrainOptions = useMemo(() => {
    const drivetrainPossibleItems = availableFilters?.find((toCheck) => toCheck.id === 'drive');
    const isFilterTagNotRemovable = isTier3page && !filterGroupsToRender?.includes('drivetrain');

    const drivetrainWithCounts = DRIVE_FILTERS.map((drivetrainValue) => {
      const drivetrainData = drivetrainPossibleItems?.possibleItems.find(
        (toCheck) => toCheck.id === drivetrainValue,
      );
      return {
        id: drivetrainValue,
        value: drivetrainValue,
        count: drivetrainData ? drivetrainData.number : 0,
        isFilterTagNotRemovable,
        disabled: !drivetrainData,
      };
    });

    return drivetrainWithCounts;
  }, [availableFilters, filterGroupsToRender, isTier3page]);

  const wheelSizeOptions = useMemo(() => {
    const wheelSizePossibleItems = availableFilters?.find((toCheck) => toCheck.id === 'wheel-size');
    const isFilterTagNotRemovable = isTier3page && !filterGroupsToRender?.includes('wheelSize');

    const wheelSizeWithCounts = WHEEL_SIZE_FILTERS.map((wheelValue) => {
      const wheelData = wheelSizePossibleItems?.possibleItems.find(
        (toCheck) => toCheck.id === wheelValue,
      );
      return {
        id: wheelValue,
        value: wheelValue,
        count: wheelData ? wheelData.number : 0,
        isFilterTagNotRemovable,
        disabled: !wheelData,
      };
    });

    return wheelSizeWithCounts;
  }, [availableFilters, filterGroupsToRender, isTier3page]);

  const gearBoxOptions = useMemo(() => {
    const gearBoxPossibleItems = availableFilters?.find((toCheck) => toCheck.id === 'gear-type');
    const isFilterTagNotRemovable = isTier3page && !filterGroupsToRender?.includes('fuelType');

    const gearboxWithCounts = GEARBOX_FILTERS.map((gearboxValue) => {
      const gearboxData = gearBoxPossibleItems?.possibleItems.find(
        (toCheck) => toCheck.id === gearboxValue,
      );
      return {
        id: gearboxValue,
        value: gearboxValue,
        count: gearboxData ? gearboxData.number : 0,
        isFilterTagNotRemovable,
        disabled: !gearboxData,
      };
    });

    return gearboxWithCounts;
  }, [availableFilters, filterGroupsToRender, isTier3page]);

  const modelYearOptions = useMemo(() => {
    const yearPossibleItems = availableFilters?.find(
      (toCheck) => toCheck.id === 'sales-model-year',
    );
    const isFilterTagNotRemovable = isTier3page && !filterGroupsToRender?.includes('modelYear');

    const numberOfYearsToShow = confirmedFilters.vehicleType === 'new' ? 2 : 17;
    const startingYear = confirmedFilters.vehicleType === 'new' ? 2025 : 2009;

    const availableYearValues = Array.from({ length: numberOfYearsToShow }, (_, i) =>
      (startingYear + i).toString(),
    ).sort((a, b) => Number(b) - Number(a));

    const yearsWithCounts = availableYearValues.map((yearValue) => {
      const yearData = yearPossibleItems?.possibleItems.find((toCheck) => toCheck.id === yearValue);
      return {
        id: yearValue,
        value: yearValue,
        count: yearData ? yearData.number : 0,
        isFilterTagNotRemovable,
        disabled: !yearData,
      };
    });

    return yearsWithCounts;
  }, [availableFilters, confirmedFilters.vehicleType, filterGroupsToRender, isTier3page]);

  // -----------------------------------------------------------------------------
  // -------------------- RANGE FILTERS ------------------------------------------

  const priceOptions = useMemo(() => {
    const isFilterTagNotRemovable = isTier3page && !filterGroupsToRender?.includes('price');
    const priceFilters = isUsedVehiclesSelected ? PRICE_FILTERS_USED : PRICE_FILTERS_NEW;
    const minValues = priceFilters.find((thCheck) => thCheck.key === 'min')?.values;
    const maxValues = priceFilters.find((thCheck) => thCheck.key === 'max')?.values;

    const minValuesObject = transformArrayFilters(minValues, 'price', isFilterTagNotRemovable);
    const maxValuesObject = transformArrayFilters(maxValues, 'price', isFilterTagNotRemovable);

    return {
      min: minValuesObject,
      max: maxValuesObject,
    };
  }, [filterGroupsToRender, isTier3page, isUsedVehiclesSelected, transformArrayFilters]);

  const powerOptions = useMemo(() => {
    const isFilterTagNotRemovable = isTier3page && !filterGroupsToRender?.includes('power');

    const minValues = POWER_FILTERS.find((thCheck) => thCheck.key === 'min')?.values;
    const maxValues = POWER_FILTERS.find((thCheck) => thCheck.key === 'max')?.values;

    const minValuesObject = transformArrayFilters(minValues, 'power', isFilterTagNotRemovable);
    const maxValuesObject = transformArrayFilters(maxValues, 'power', isFilterTagNotRemovable);

    return {
      min: minValuesObject,
      max: maxValuesObject,
    };
  }, [filterGroupsToRender, isTier3page, transformArrayFilters]);

  const mileageOptions = useMemo(() => {
    const isFilterTagNotRemovable = isTier3page && !filterGroupsToRender?.includes('mileage');

    const minValues = MILEAGE_FILTERS.find((thCheck) => thCheck.key === 'min')?.values;
    const maxValues = MILEAGE_FILTERS.find((thCheck) => thCheck.key === 'max')?.values;

    const minValuesObject = transformArrayFilters(minValues, 'mileage', isFilterTagNotRemovable);
    const maxValuesObject = transformArrayFilters(maxValues, 'mileage', isFilterTagNotRemovable);

    return {
      min: minValuesObject,
      max: maxValuesObject,
    };
  }, [filterGroupsToRender, isTier3page, transformArrayFilters]);

  // Tier 3 Specifc
  const otherMakesOptions = useMemo(() => {
    if (!isUsedVehiclesSelected) return null;
    const nonAudiManufactures = availableFilters?.find((toCheck) => toCheck.id === 't_manuf');

    const otherMakes = nonAudiManufactures?.possibleItems?.reduce((acc: any, item: any) => {
      const { id, number } = item;
      const makeCode = id as keyof typeof nonAudiBrandsFullList;
      acc[id] = {
        active: true,
        code: id,
        count: number ?? 0,
        value: id,
        label: nonAudiBrandsFullList[makeCode]?.text,
        ariaLabel: nonAudiBrandsFullList[makeCode]?.text,
      };
      return acc;
    }, {});
    return otherMakes;
  }, [availableFilters, isUsedVehiclesSelected]);

  const nonAudiModelsOptions = useMemo(() => {
    if (!isUsedVehiclesSelected) return null;
    const nonAudiModels = availableFilters?.find((toCheck) => toCheck.id === 't_model');

    const fullListOfNonAudiModels = Object.values(nonAudiBrandsFullList).reduce(
      (acc, { members }) => {
        return { ...acc, ...members };
      },
      {},
    ) as NonAudiModelFullList;

    const filteredList = nonAudiModels?.possibleItems.filter((model) => {
      const modelCode = model?.id.substring(2);
      // Unknown Model Code should not be filtered out
      if (modelCode === 'ZZ') {
        return true;
      }
      return fullListOfNonAudiModels[model.id]?.text;
    });
    if (!filteredList?.length) return null;

    const listOfAvailableNonAudiModels = filteredList?.reduce((acc: any, item: any) => {
      const { id, number } = item;
      const modelCode = id as keyof typeof fullListOfNonAudiModels;
      const makeCode = id.slice(0, 2) as keyof typeof nonAudiBrandsFullList;
      const makeLabel = nonAudiBrandsFullList[makeCode]?.text;
      const isUnknownModelCode = modelCode?.toString()?.substring(2) === 'ZZ';
      // If Model Code is ZZ then it means it has an unknown model code so we should show "Other as Label"
      const label = isUnknownModelCode
        ? t('vlp.filters.filterOptions.nonAudiModels.other')
        : fullListOfNonAudiModels[modelCode]?.text;
      acc[id] = {
        active: true,
        code: id,
        count: number ?? 0,
        value: id,
        label,
        ariaLabel: label,
        makeLabel,
        makeCode,
      };
      return acc;
    }, {});
    return listOfAvailableNonAudiModels;
  }, [availableFilters, isUsedVehiclesSelected, t]);

  // -----------------------------------------------------------------------------
  // -------------------- SORT MEYHODS _------------------------------------------

  const sortMethods = useMemo(
    () => ({
      ...(isTier3page
        ? {
            'DATE_PREDATEEND:ASC': {
              label: t('vlp.vehicleResults.sort.filterOptions.byOldestInLot'),
              id: 'DATE_OFFER',
              direction: 'ASC',
              value: 'DATE_PREDATEEND:ASC',
            },
          }
        : {}),
      'salesModelYear:ASC': {
        label: t('vlp.vehicleResults.sort.filterOptions.yearAscending'),
        id: 'MODELYEAR',
        direction: 'ASC',
        value: 'salesModelYear:ASC',
      },
      'salesModelYear:DESC': {
        label: t('vlp.vehicleResults.sort.filterOptions.yearDescending'),
        id: 'MODELYEAR',
        direction: 'DESC',
        value: 'salesModelYear:DESC',
      },
      'prices.retail:ASC': {
        label: t('vlp.vehicleResults.sort.filterOptions.priceAscending'),
        id: 'PRICE_SALE',
        direction: 'ASC',
        value: 'prices.retail:ASC',
      },
      'prices.retail:DESC': {
        label: t('vlp.vehicleResults.sort.filterOptions.priceDescending'),
        id: 'PRICE_SALE',
        direction: 'DESC',
        value: 'prices.retail:DESC',
      },
      ...(!isTier3page
        ? {
            'byDistance:ASC': {
              label: t('vlp.vehicleResults.sort.filterOptions.distanceAscending'),
              id: 'BY_DISTANCE',
              direction: 'ASC',
              value: 'byDistance:ASC',
            },
          }
        : {}),
      ...(!isTier3page
        ? {
            'byDistance:DESC': {
              label: t('vlp.vehicleResults.sort.filterOptions.distanceDescending'),
              id: 'BY_DISTANCE',
              direction: 'DESC',
              value: 'byDistance:DESC',
            },
          }
        : {}),
      ...(isUsedVehiclesSelected
        ? {
            'mileage:ASC': {
              label: t('vlp.vehicleResults.sort.filterOptions.mileageAscending'),
              id: 'MILEAGE',
              direction: 'ASC',
              value: 'mileage:ASC',
            },
          }
        : {}),
      ...(isUsedVehiclesSelected
        ? {
            'mileage:DESC': {
              label: t('vlp.vehicleResults.sort.filterOptions.mileageDescending'),
              id: 'MILEAGE',
              direction: 'DESC',
              value: 'mileage:DESC',
            },
          }
        : {}),
    }),
    [isTier3page, isUsedVehiclesSelected, t],
  );

  const listOfAllFilters = {
    vehicleType: vehicleTypeOptions,
    newInventoryType: newInventoryTypeOptions,
    usedInventoryType: usedInventoryTypeOptions,
    modelFamily: modelFamilyOptions,
    bodyType: bodyTypeOptions,
    trimLine: trimlineOptions,
    gearBox: gearBoxOptions,
    drivetrain: drivetrainOptions,
    fuelType: fuelTypeOptions,
    wheelSize: wheelSizeOptions,
    exteriorColor: exteriorColorOptions,
    interiorColor: interiorColorOptions,
    power: powerOptions,
    mileage: mileageOptions,
    price: priceOptions,
    modelYear: modelYearOptions,
    partnerDealers: listOfDealers,
    otherMakes: otherMakesOptions,
    nonAudiModels: nonAudiModelsOptions,
  };

  return {
    listOfAllFilters,
    vehicleTypeOptions,
    newInventoryTypeOptions,
    usedInventoryTypeOptions,
    bodyTypeOptions,
    modelFamilyOptions,
    trimlineOptions,
    exteriorColorOptions,
    interiorColorOptions,
    fuelTypeOptions,
    gearBoxOptions,
    wheelSizeOptions,
    drivetrainOptions,
    modelYearOptions,
    mileageOptions,
    powerOptions,
    priceOptions,
    sortMethods,
    otherMakesOptions,
    nonAudiModelsOptions,
  };
};

export default useFilterOptions;
